import React, { useState, useEffect } from "react";
import { getProducts } from "../api/api-hook";

interface VipProduct {
  id: string;
  name: string;
  label: string;
  description?: string;
  price?: number;
  product_category: string;
  tabKey: string;
  imageUrl: string;
  under_25: string;
  product_id: string;
}

interface GiftSectionProps {
  onGiftChange?: (hasGift: boolean) => void;
  onGiftProductChange?: (productId: string | null) => void;
  onGiftTabChange?: (tabKey: string | null) => void;
}

const GiftSection: React.FC<GiftSectionProps> = ({
  onGiftChange,
  onGiftProductChange,
  onGiftTabChange,
}) => {
  const [selectedAmount, setSelectedAmount] = useState<number | null>(null);
  const [isYouthConfirmed, setIsYouthConfirmed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState<VipProduct[]>([]);

  const descriptionToList = (description: string) => {
    if (!description) return null;

    const blocks = description.split(/\r?\n\r?\n+/);

    return (
      <div className="mb-3 gift-description">
        {blocks.map((block, index) => {
          const lines = block
            .split(/\r?\n/)
            .map((line) => line.trim())
            .filter(Boolean);

          if (lines.length === 1) {
            return (
              <div key={index} className="mb-2">
                {lines[0]}
              </div>
            );
          }

          return (
            <ul key={index} className="mb-3">
              {lines.map((line, i) => (
                <li key={i}>{line}</li>
              ))}
            </ul>
          );
        })}
      </div>
    );
  };

  useEffect(() => {
    async function loadProducts() {
      try {
        setLoading(true);
        setError(null);
const raw = await getProducts({
  property: "product_category",
  value: "vip",
});

console.log("GiftSection raw response:", raw);  // ADD THIS
console.log("GiftSection typeof raw:", typeof raw);  // ADD THIS

const parsed: unknown =
  typeof raw === "string" ? JSON.parse(raw) : raw;

console.log("GiftSection parsed:", parsed);  // ADD THIS
console.log("GiftSection isArray:", Array.isArray(parsed));  // ADD THIS

        // CRITICAL FIX: guard against non-array API response
        if (!Array.isArray(parsed)) {
          console.error("GiftSection: expected array, got", parsed);
          setProducts([]);
          return;
        }

        const ordered = [...parsed].sort(
          (a, b) => (a.price ?? 9999) - (b.price ?? 9999)
        );

        setProducts(ordered);
      } catch (err) {
        console.error(err);
        setError("Failed to load products");
        setProducts([]); // CRITICAL FIX: always reset to [] on error
      } finally {
        setLoading(false);
      }
    }

    loadProducts();
  }, []);

  const handleSelectAmount = (amount: number) => {
    setSelectedAmount(amount);

    if (amount !== 75) {
      setIsYouthConfirmed(false);
    }

    onGiftChange?.(!!amount);

    const matchedProduct = products.find(
      (p) => Number(p.price) === Number(amount)
    );

    onGiftProductChange?.(matchedProduct ? matchedProduct.id : null);
    onGiftTabChange?.(matchedProduct ? matchedProduct.tabKey : null);
  };

  // CRITICAL FIX: always ensure products is an array before any .map
  const safeProducts = Array.isArray(products) ? products : [];

  return (
    <div className="mb-4">
      <h3 className="mb-2">Gift Amount</h3>
      <p className="text-muted mb-3">
        Give the gift of a VSGA VIP Card! Choose a gift card amount based on
        any VIP Card level. You'll receive a printable gift certificate, and the
        recipient will be emailed a promo code to redeem their VIP Card. The
        recipient must complete the redemption process at checkout before their
        VIP Card becomes active.
      </p>

      <div className="row g-2">
        {safeProducts.map((product) => {
          const isActive = selectedAmount === product.price;

          return (
            <div
              className="col-4 col-md-2 d-flex justify-content-center"
              key={product.price}
            >
              <button
                type="button"
                className={
                  "w-100 btn btn-outline-secondary text-center py-2 " +
                  (isActive ? "active border-2" : "")
                }
                onClick={() => handleSelectAmount(product.price || 0)}
              >
                <div className="fw-semibold" style={{ fontSize: "0.95rem" }}>
                  ${product.price}
                </div>
              </button>
            </div>
          );
        })}
      </div>

      <input type="hidden" name="giftAmount" value={selectedAmount ?? ""} />
      <input
        type="hidden"
        name="giftIsYouth18to25"
        value={
          selectedAmount === 75 && isYouthConfirmed ? "true" : "false"
        }
      />

      {selectedAmount && (
        <div className="mt-4 pt-2">
          {safeProducts
            .filter(
              (product) =>
                Number(product.price) === Number(selectedAmount)
            )
            .map((product) => (
              <div key={product.id} className="mb-3">
                <h3 className="mb-4">
                  {product.label || product.name} – $
                  {product.price != null
                    ? product.price.toFixed(2)
                    : ""}
                </h3>

                {descriptionToList &&
                  descriptionToList(product.description ?? "")}

                {product.under_25 && (
                  <div className="form-check mt-2">
                    <input
                      type="checkbox"
                      id="age"
                      className="form-check-input"
                      required
                    />
                    <label
                      htmlFor="ageVerify"
                      className="form-check-label"
                    >
                      I confirm this gift is for someone age 18–25.
                    </label>
                  </div>
                )}

                <div className="col-12 col-sm-5 col-lg-3 mt-4">
                  <button
                    type="submit"
                    className="btn btn-primary w-100"
                  >
                    Continue
                  </button>
                </div>
              </div>
            ))}

          {safeProducts.filter(
            (p) => Number(p.price) === Number(selectedAmount)
          ).length === 0 && (
            <p className="text-muted mb-0">
              No gift options are available at this amount yet.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default GiftSection;